import moment from "moment";

// Static start date for week generation
export const WEEKS_START_DATE = "2026-02-16";

export interface WeekData {
  weekNumber: number;
  startDate: string; // Monday (YYYY-MM-DD)
  endDate: string; // Sunday (YYYY-MM-DD)
  label: string; // "Current Week", "Last Week", etc.
  isCurrentWeek: boolean;
}

/**
 * Get the start of the week (Monday) for a given date
 */
const getWeekStart = (date: moment.Moment): moment.Moment => {
  const dayOfWeek = date.day(); // 0 = Sunday, 1 = Monday, 6 = Saturday
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  return date.clone().subtract(daysToSubtract, "days").startOf("day");
};

/**
 * Get the end of the week (Sunday) for a given date
 */
const getWeekEnd = (weekStart: moment.Moment): moment.Moment => {
  return weekStart.clone().add(6, "days").endOf("day");
};

/**
 * Generate weeks from WEEKS_START_DATE until current week
 * Each week starts on Monday and ends on Sunday
 */
export const generateWeeks = (): WeekData[] => {
  const weeks: WeekData[] = [];
  const today = moment();
  const currentWeekStart = getWeekStart(today);
  const startDateMoment = moment(WEEKS_START_DATE);
  const firstWeekStart = getWeekStart(startDateMoment);

  let weekStart = currentWeekStart.clone();
  let weekCounter = 0;

  while (weekStart.isSameOrAfter(firstWeekStart, "day")) {
    const weekEnd = getWeekEnd(weekStart);
    const isCurrent = weekStart.isSame(currentWeekStart, "day");

    let label = "";
    if (weekCounter === 0) {
      label = "Current Week";
    } else if (weekCounter === 1) {
      label = "Last Week";
    } else {
      label = `${weekCounter} Weeks Ago`;
    }

    weeks.push({
      weekNumber: weekCounter + 1,
      startDate: weekStart.format("YYYY-MM-DD"),
      endDate: weekEnd.format("YYYY-MM-DD"),
      label,
      isCurrentWeek: isCurrent,
    });

    weekStart = weekStart.clone().subtract(1, "week");
    weekCounter++;
  }

  return weeks;
};

/**
 * Calculate the countdown to the end of the current week (Sunday 11:59 PM)
 * Returns an object with days, hours, minutes, and seconds remaining
 */
export const calculateCountdown = () => {
  const now = moment();
  const currentWeekStart = getWeekStart(now);
  const currentWeekEnd = getWeekEnd(currentWeekStart);

  const duration = moment.duration(currentWeekEnd.diff(now));

  if (duration.asMilliseconds() <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
  }

  return {
    days: Math.floor(duration.asDays()),
    hours: duration.hours(),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
    isExpired: false,
  };
};

/**
 * Format week range for display (e.g., "Mar 2 - 8, 2026")
 */
export const formatWeekRange = (startDate: string, endDate: string): string => {
  const start = moment(startDate);
  const end = moment(endDate);
  
  if (start.month() === end.month()) {
    return `${start.format("MMM DD")} - ${end.format("DD, YYYY")}`;
  }

  return `${start.format("MMM DD")} - ${end.format("MMM DD, YYYY")}`;
};
